import { get } from "@libs:axios";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { CamelCasedPropertiesDeep } from "type-fest";

import {
  GetFeedbackSummaryApiAnalyticsFeedbackGetData,
  GetSummaryApiAnalyticsSummaryGetData,
  GetTopApiAnalyticsTopGetData,
} from "@:openapi";

export type AnalyticsActiveUsers = {
  date: string;
  numLogins: number;
};

export type AnalyticsRequests = {
  date: string;
  numRequests: number;
};

export type AnalyticsSummary = {
  activeUsersYesterday: AnalyticsActiveUsers[];
  activeUsersPeriod: AnalyticsActiveUsers[];
  activeUsersPeriodDistinct: number;
  requestsYesterday: AnalyticsRequests[];
  requestsPeriod: AnalyticsRequests[];
};

export type AnalyticsTopUserRequests = {
  userId: string;
  fullName: string;
  email: string;
  numRequests: number;
};

export type AnalyticsTopDocuments = {
  document: string;
  numReferences: number;
};

export type AnalyticsTop = AnalyticsTopUserRequests[] | AnalyticsTopDocuments[];

export type AnalyticsFeedbackResponse = {
  total: number;
  positive: number;
  negative: number;
};

export interface Analytics {
  GET_SUMMARY: {
    RequestQuery: CamelCasedPropertiesDeep<
      GetSummaryApiAnalyticsSummaryGetData["query"]
    >;
    Response: AnalyticsSummary;
  };
  GET_TOP: {
    RequestQuery: CamelCasedPropertiesDeep<
      GetTopApiAnalyticsTopGetData["query"]
    >;
    Response: AnalyticsTop;
  };
  GET_FEEDBACK: {
    RequestQuery: CamelCasedPropertiesDeep<
      GetFeedbackSummaryApiAnalyticsFeedbackGetData["query"]
    >;
    Response: AnalyticsFeedbackResponse;
  };
}

export const analyticsQueryKey = createQueryKeys("analytics", {
  getSummary: (params: Analytics["GET_SUMMARY"]["RequestQuery"]) => [params],
  getTop: (params: Analytics["GET_TOP"]["RequestQuery"]) => [params],
  getFeedback: (params: Analytics["GET_FEEDBACK"]["RequestQuery"]) => [params],
});

export type AnalyticsSummarySelector = {
  activeUsersYesterday: number;
  activeUsersPeriod: number;
  activeUsersPeriodDistinct: number;
  activeUsersPeriodData: AnalyticsActiveUsers[];
  requestsYesterday: number;
  requestsPeriod: number;
  requestsPeriodData: AnalyticsRequests[];
};

export const useQueryAnalyticsSummary = (
  params: Analytics["GET_SUMMARY"]["RequestQuery"],
  options?: Omit<
    UseQueryOptions<
      Analytics["GET_SUMMARY"]["Response"],
      AxiosError,
      AnalyticsSummarySelector
    >,
    "queryKey"
  >,
) => {
  return useQuery<
    Analytics["GET_SUMMARY"]["Response"],
    AxiosError,
    AnalyticsSummarySelector
  >({
    queryKey: analyticsQueryKey.getSummary(params).queryKey,
    queryFn: () => {
      return get<Analytics["GET_SUMMARY"]["Response"]>(
        `/api/analytics/summary`,
        params,
      );
    },
    ...options,
  });
};

export const useQueryAnalyticsTop = (
  params: Analytics["GET_TOP"]["RequestQuery"],
  options?: Omit<
    UseQueryOptions<Analytics["GET_TOP"]["Response"], AxiosError>,
    "queryKey"
  >,
) => {
  return useQuery<Analytics["GET_TOP"]["Response"], AxiosError>({
    queryKey: analyticsQueryKey.getTop(params).queryKey,
    queryFn: () => {
      return get<Analytics["GET_TOP"]["Response"]>(
        `/api/analytics/top`,
        params,
      );
    },
    ...options,
  });
};

export const useQueryAnalyticsFeedback = (
  params: Analytics["GET_FEEDBACK"]["RequestQuery"],
  options?: Omit<
    UseQueryOptions<Analytics["GET_FEEDBACK"]["Response"], AxiosError>,
    "queryKey"
  >,
) => {
  return useQuery<Analytics["GET_FEEDBACK"]["Response"], AxiosError>({
    queryKey: analyticsQueryKey.getFeedback(params).queryKey,
    queryFn: () => {
      return get<Analytics["GET_FEEDBACK"]["Response"]>(
        `/api/analytics/feedback`,
        params,
      );
    },
    ...options,
  });
};
